import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { Edit2, Trash } from "iconsax-react";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";

import { ProductIconTile, SefanoIconButton, StatusBadge } from "@/components/Atoms";
import type { StatusBadgeColor } from "@/components/Atoms/StatusBadge/StatusBadge.types";
import { formatPersianCurrency, formatPersianNumber } from "@/lib/format/persian";
import type { Product } from "@/api/schemas/product";
import type { ProductsTableProps } from "./ProductsTable.types";
import * as styles from "./ProductsTable.styles";

export type { ProductsTableProps } from "./ProductsTable.types";

function getProductStatus(product: Product): { labelKey: string; color: StatusBadgeColor } {
  if (!product.is_active) {
    return { labelKey: "status.inactive", color: "neutral" };
  }
  if (product.stock <= 0) {
    return { labelKey: "status.outOfStock", color: "error" };
  }
  if (product.stock <= 5) {
    return { labelKey: "status.lowStock", color: "warning" };
  }
  return { labelKey: "status.inStock", color: "success" };
}

function getStockColor(product: Product, theme: Theme): string {
  if (product.stock <= 0) return theme.palette.error.main;
  if (product.stock <= 5) return theme.palette.warning.main;
  return theme.palette.text.primary;
}

export const ProductsTable = memo(function ProductsTable({
  products,
  categoryNames = {},
  isLoading = false,
  onDelete,
  layout = "responsive",
}: ProductsTableProps) {
  const { t } = useTranslation("products");
  const theme = useTheme();

  const showDesktop = layout === "desktop" || layout === "responsive";
  const showMobile = layout === "mobile" || layout === "responsive";
  const desktopIconSize = 42;
  const mobileIconSize = 46;

  const handleDelete = useCallback(
    (event: React.MouseEvent, productId: number) => {
      event.stopPropagation();
      onDelete?.(productId);
    },
    [onDelete],
  );

  const stockColorFn = useMemo(
    () => (product: Product) => getStockColor(product, theme),
    [theme],
  );

  if (isLoading) {
    return (
      <Box sx={styles.loadingWrap}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <>
      {showDesktop ? (
        <Box sx={styles.desktopTable(layout)}>
          <Table sx={styles.table}>
            <TableHead>
              <TableRow>
                <TableCell sx={styles.th}>{t("columns.product")}</TableCell>
                <TableCell sx={styles.th}>{t("columns.category")}</TableCell>
                <TableCell sx={styles.th}>{t("columns.price")}</TableCell>
                <TableCell sx={styles.th} align="center">
                  {t("columns.stock")}
                </TableCell>
                <TableCell sx={styles.th} align="center">
                  {t("columns.status")}
                </TableCell>
                <TableCell sx={styles.th} align="center">
                  {t("columns.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => {
                const status = getProductStatus(product);
                const categoryName =
                  product.category_id != null
                    ? categoryNames[product.category_id] ?? "—"
                    : "—";
                return (
                  <TableRow key={product.id}>
                    <TableCell sx={styles.td}>
                      <Box sx={styles.productCell}>
                        <ProductIconTile size={desktopIconSize} imageUrl={product.image_url} alt={product.name} />
                        <Typography variant="body2" fontWeight={600}>
                          {product.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={styles.tdCategory}>{categoryName}</TableCell>
                    <TableCell sx={styles.tdAmount}>
                      {formatPersianCurrency(product.price).replace(" ریال", "")}
                    </TableCell>
                    <TableCell sx={styles.stockTd(stockColorFn(product))} align="center">
                      {formatPersianNumber(product.stock)}
                    </TableCell>
                    <TableCell sx={styles.td} align="center">
                      <StatusBadge color={status.color} size="sm" label={t(status.labelKey)} />
                    </TableCell>
                    <TableCell sx={styles.td} align="center">
                      <Box sx={styles.actionsCell}>
                        <Link
                          to="/products/$productId/edit"
                          params={{ productId: String(product.id) }}
                          style={{ textDecoration: "none" }}
                        >
                          <SefanoIconButton aria-label={t("columns.edit")}>
                            <Edit2 size={17} variant="Linear" />
                          </SefanoIconButton>
                        </Link>
                        <SefanoIconButton
                          variant="danger"
                          onClick={(event) => handleDelete(event, product.id)}
                          aria-label={t("columns.actions")}
                        >
                          <Trash size={17} variant="Linear" />
                        </SefanoIconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ) : null}

      {showMobile ? (
        <Box sx={styles.mobileList(layout)}>
          {products.map((product) => {
            const status = getProductStatus(product);
            const price = formatPersianCurrency(product.price).replace(" ریال", "");
            return (
              <Box key={product.id} sx={styles.mobileCard}>
                <ProductIconTile size={mobileIconSize} imageUrl={product.image_url} alt={product.name} />
                <Box sx={styles.mobileInfo}>
                  <Box sx={styles.mobileTopRow}>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
                      {product.name}
                    </Typography>
                    <StatusBadge color={status.color} size="sm" label={t(status.labelKey)} />
                  </Box>
                  <Box sx={styles.mobileBottomRow}>
                    <Typography sx={styles.mobilePrice}>
                      {price}{" "}
                      <Typography component="span" sx={styles.mobileCurrency}>
                        ریال
                      </Typography>
                    </Typography>
                    <Typography sx={styles.mobileStock}>
                      {t("columns.stock")}:{" "}
                      <Typography
                        component="span"
                        fontWeight={600}
                        sx={{ color: stockColorFn(product) }}
                      >
                        {formatPersianNumber(product.stock)}
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : null}
    </>
  );
});
